import { create } from 'zustand'

const CHANGE_HIGHLIGHT_WINDOW_MS = 5000

function resolveJobStatus(job) {
  const value = String(
    job?.job_status
    ?? job?.jobStatus
    ?? job?.status
    ?? job?.ocr_status
    ?? job?.ocrStatus
    ?? ''
  ).toLowerCase()
  return value || 'unknown'
}

export const useDashboardStore = create((set) => ({
  snapshot: null,
  systemHealth: null,
  queues: [],
  pipelines: [],
  recentJobs: [],
  lastErrors: [],
  dataSummary: {},
  jobs: [],
  metrics: {},
  queueProcessing: {},
  recentChanges: {},
  isLoading: true,
  isRefreshing: false,
  error: '',
  lastUpdatedAt: null,
  // Backward-compat aliases for current dashboard component.
  loading: true,
  updatedAt: null,

  setLoading: (loading) => set({
    isLoading: Boolean(loading),
    loading: Boolean(loading),
  }),
  setError: (error) => set({ error: String(error || '') }),
  clearError: () => set({ error: '' }),
  setSnapshot: (snapshot) =>
    set(() => {
      const nowIso = new Date().toISOString()
      const safeSnapshot = snapshot && typeof snapshot === 'object' ? snapshot : {}
      return {
        snapshot: safeSnapshot,
        systemHealth: safeSnapshot.systemHealth || null,
        queues: Array.isArray(safeSnapshot.queues) ? safeSnapshot.queues : [],
        pipelines: Array.isArray(safeSnapshot.pipelines) ? safeSnapshot.pipelines : [],
        recentJobs: Array.isArray(safeSnapshot.recentJobs) ? safeSnapshot.recentJobs : [],
        lastErrors: Array.isArray(safeSnapshot.lastErrors) ? safeSnapshot.lastErrors : [],
        dataSummary: safeSnapshot.dataSummary || {},
        isLoading: false,
        isRefreshing: false,
        loading: false,
        error: '',
        lastUpdatedAt: safeSnapshot.generatedAt || nowIso,
        updatedAt: safeSnapshot.generatedAt || nowIso,
      }
    }),
  loadInitialSnapshot: async (fetchSnapshot) => {
    set({ isLoading: true, loading: true, error: '' })
    try {
      const snapshot = await fetchSnapshot()
      useDashboardStore.getState().setSnapshot(snapshot)
    } catch (error) {
      set({
        isLoading: false,
        loading: false,
        isRefreshing: false,
        error: error?.message || String(error) || 'Failed to load dashboard snapshot',
      })
    }
  },
  refreshSnapshot: async (fetchSnapshot) => {
    set({ isRefreshing: true })
    try {
      const snapshot = await fetchSnapshot()
      useDashboardStore.getState().setSnapshot(snapshot)
    } catch (error) {
      // WHY: refresh failure must not blank current snapshot.
      set({
        isRefreshing: false,
        error: error?.message || String(error) || 'Failed to refresh dashboard snapshot',
      })
    }
  },

  // Keep queues/jobs/metrics in one store so dashboard sections share a single source of truth.
  applySnapshot: ({ queues = [], jobs = [], metrics = {}, queueProcessing = {} }) =>
    set((state) => {
      const now = Date.now()
      const previousById = new Map((state.jobs || []).map((job) => [job.id, resolveJobStatus(job)]))

      const retainedChanges = Object.fromEntries(
        Object.entries(state.recentChanges || {}).filter(([, meta]) => now - Number(meta?.timestamp || 0) <= CHANGE_HIGHLIGHT_WINDOW_MS)
      )

      const nextChanges = { ...retainedChanges }
      for (const job of jobs) {
        const id = job?.id
        if (!id) continue
        const nextStatus = resolveJobStatus(job)
        const prevStatus = previousById.get(id)
        if (!prevStatus) continue
        if (prevStatus === nextStatus) continue
        if (nextStatus !== 'failed' && nextStatus !== 'completed') continue
        nextChanges[id] = { kind: nextStatus, timestamp: now }
      }

      return {
        snapshot: state.snapshot,
        queues,
        jobs,
        metrics,
        queueProcessing,
        systemHealth: state.systemHealth,
        pipelines: state.pipelines,
        recentJobs: state.recentJobs,
        lastErrors: state.lastErrors,
        dataSummary: state.dataSummary,
        recentChanges: nextChanges,
        isLoading: false,
        isRefreshing: false,
        loading: false,
        error: '',
        lastUpdatedAt: new Date(now).toISOString(),
        updatedAt: new Date(now).toISOString(),
      }
    }),

  clearJobChange: (jobId) =>
    set((state) => {
      const next = { ...(state.recentChanges || {}) }
      delete next[jobId]
      return { recentChanges: next }
    }),

  resetStore: () => set({
    snapshot: null,
    systemHealth: null,
    queues: [],
    pipelines: [],
    recentJobs: [],
    lastErrors: [],
    dataSummary: {},
    jobs: [],
    metrics: {},
    queueProcessing: {},
    recentChanges: {},
    isLoading: true,
    isRefreshing: false,
    loading: true,
    error: '',
    lastUpdatedAt: null,
    updatedAt: null,
  }),
}))
